import { Children, isValidElement, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import type { Tone } from './Pill'
import s from './Chip.module.css'

/**
 * Chip — an interactive, toggleable pill (facets, filters like "</> MCP", view
 * toggles). `active` reads as the brand-tinted on-state. For a removable value
 * chip (a committed filter), use FilterChip.
 */
export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  active?: boolean
  /**
   * The PRESSED state's semantic fill. Default `brand`, which is the on-state
   * this control has always had, so every existing call site is unchanged.
   *
   * It exists because an interactive pill whose pressed state carries a MEANING
   * — a verdict, an outcome, a kind — was hand-drawn in at least four places
   * and never expressible here: CRM's Won/Lost caps, this package's own
   * `ElevateTimelineToolbar` (four per-kind fills), and the Approve/Reject pair
   * on the approvals, time-review and returns desks, where the destructive
   * verdict is currently drawn as the QUIETER of the two.
   *
   * It reuses the estate's `Tone` rather than inventing `positive | negative`:
   * a two-polarity enum would be wrong on the first host with three outcomes,
   * which is the argument `StatusRail` already makes for owning none of this.
   */
  tone?: Tone
  children: ReactNode
}
export function Chip({ active, tone = 'brand', className, children, ...rest }: ChipProps) {
  return (
    <button type="button" className={cx(s.chip, active && s.on, active && s[tone], className)}
      aria-pressed={active} {...rest}>
      {children}
    </button>
  )
}

/** A committed filter value with a remove affordance (span + inner ×, valid HTML). */
/**
 * A VALUE CHIP WITH ITS OWN REMOVE CONTROL.
 *
 * `className` and rest props reach the element, and that is not housekeeping:
 * without them a host that needs a test id or one layout rule on the chip has
 * to WRAP it in a span of its own — and once there is a span, it gets a
 * padding, a border, a radius and a background, because the wrapper looks
 * naked beside the thing it contains. `RelationshipWorkspace` had exactly
 * that: a chip drawn around the kit's chip, plus a re-style of this control's
 * own remove button. Two chips, one value.
 */
/*
 * ── THE REMOVE CONTROL SAYS WHAT IT REMOVES (#2406 b3-controls) ───────────
 * It was named "Remove filter" on every chip, whatever the chip held, so a
 * screen-reader user walking a row of three filters heard the same name three
 * times and could not tell which one a press would drop — and the relationship
 * workspace's basket, which holds RECORDS, announced "Remove filter" on a
 * person. The name is now "Remove <what the chip says>", read from the chip's
 * own words; a host whose children are not words (an icon, a composite) or
 * that wants other words passes `removeLabel`.
 */
export function FilterChip({ children, onRemove, removeLabel, className, ...rest }: {
  children: ReactNode
  onRemove?: () => void
  /** The remove button's accessible name. Defaults to "Remove <the chip's text>". */
  removeLabel?: string
  className?: string
} & Omit<HTMLAttributes<HTMLSpanElement>, 'className'>) {
  const words = textOf(children).replace(/\s+/g, ' ').trim()
  return (
    <span className={cx(s.chip, s.on, className)} {...rest}>
      {children}
      {onRemove && (
        <button type="button" className={s.x} aria-label={removeLabel ?? (words ? `Remove ${words}` : 'Remove')}
          onClick={(e) => { e.stopPropagation(); onRemove() }}>×</button>
      )}
    </span>
  )
}

/** The text a node would show: strings and numbers, through fragments and elements. */
function textOf(node: ReactNode): string {
  let out = ''
  Children.forEach(node, (child) => {
    if (typeof child === 'string' || typeof child === 'number') out += String(child)
    else if (isValidElement<{ children?: ReactNode }>(child)) out += textOf(child.props.children)
  })
  return out
}
